import { z } from "zod"
import { ProposalLevel } from "./proposal-level-kind"

/**
 * The A/B tests on the customer's proposal screen. Each splits jobs evenly
 * over its variants (each new job goes to the one with the fewest so far), assigned the first time the cyclist opens the proposal
 * and kept for good, so everything in one proposal is in the same variant.
 *
 * Only what the cyclist saw in full counts (an exposure), and each exposure
 * gets an outcome once the proposal is approved.
 */
export enum ProposalAbTest {
  /** The "Lagkrav" badge on part/action cards in "Serviceförslag". */
  SERVICE_LEGAL_BADGE = "SERVICE_LEGAL_BADGE",
  /** The "Lagkrav" badge on the equipment cards in "Tillval". */
  UPSELL_LEGAL_BADGE = "UPSELL_LEGAL_BADGE",
  /**
   * Which level "Välj servicenivå" starts on — Toppskick, Trafiksäker or
   * none — each with no "Rekommenderad", "Rekommenderad" on Toppskick or
   * "Rekommenderad" on Trafiksäker. Only jobs whose levels are far enough
   * apart are in it.
   */
  SERVICE_LEVEL_DEFAULT = "SERVICE_LEVEL_DEFAULT",
}

export enum LegalBadgeVariant {
  /** As before the test. */
  SHOWN = "SHOWN",
  HIDDEN = "HIDDEN",
}

export enum LevelDefaultVariant {
  /** As before the test. */
  TOP_CONDITION = "TOP_CONDITION",
  TRAFFIC_SAFE = "TRAFFIC_SAFE",
  /** Nothing selected: the cyclist has to pick a level to approve. */
  NONE = "NONE",
  TOP_CONDITION_RECOMMENDED = "TOP_CONDITION_RECOMMENDED",
  TRAFFIC_SAFE_RECOMMENDED = "TRAFFIC_SAFE_RECOMMENDED",
  NONE_RECOMMENDED = "NONE_RECOMMENDED",
  /** Trafiksäker selected, and marked "Rekommenderad" instead of Toppskick. */
  TRAFFIC_SAFE_RECOMMENDED_ON_TRAFFIC_SAFE = "TRAFFIC_SAFE_RECOMMENDED_ON_TRAFFIC_SAFE",
  /** Toppskick selected, Trafiksäker marked "Rekommenderad". */
  TOP_CONDITION_RECOMMENDED_ON_TRAFFIC_SAFE = "TOP_CONDITION_RECOMMENDED_ON_TRAFFIC_SAFE",
  /** Nothing selected, Trafiksäker marked "Rekommenderad". */
  NONE_RECOMMENDED_ON_TRAFFIC_SAFE = "NONE_RECOMMENDED_ON_TRAFFIC_SAFE",
}

/** What a level-default variant shows. */
export const LEVEL_DEFAULT_VARIANT_FLAGS: Record<
  LevelDefaultVariant,
  { defaultLevel: ProposalLevel | null; recommendedLevel: ProposalLevel | null }
> = {
  [LevelDefaultVariant.TOP_CONDITION]: {
    defaultLevel: ProposalLevel.TOP_CONDITION,
    recommendedLevel: null,
  },
  [LevelDefaultVariant.TRAFFIC_SAFE]: {
    defaultLevel: ProposalLevel.TRAFFIC_SAFE,
    recommendedLevel: null,
  },
  [LevelDefaultVariant.NONE]: {
    defaultLevel: null,
    recommendedLevel: null,
  },
  [LevelDefaultVariant.TOP_CONDITION_RECOMMENDED]: {
    defaultLevel: ProposalLevel.TOP_CONDITION,
    recommendedLevel: ProposalLevel.TOP_CONDITION,
  },
  [LevelDefaultVariant.TRAFFIC_SAFE_RECOMMENDED]: {
    defaultLevel: ProposalLevel.TRAFFIC_SAFE,
    recommendedLevel: ProposalLevel.TOP_CONDITION,
  },
  [LevelDefaultVariant.NONE_RECOMMENDED]: {
    defaultLevel: null,
    recommendedLevel: ProposalLevel.TOP_CONDITION,
  },
  [LevelDefaultVariant.TRAFFIC_SAFE_RECOMMENDED_ON_TRAFFIC_SAFE]: {
    defaultLevel: ProposalLevel.TRAFFIC_SAFE,
    recommendedLevel: ProposalLevel.TRAFFIC_SAFE,
  },
  [LevelDefaultVariant.TOP_CONDITION_RECOMMENDED_ON_TRAFFIC_SAFE]: {
    defaultLevel: ProposalLevel.TOP_CONDITION,
    recommendedLevel: ProposalLevel.TRAFFIC_SAFE,
  },
  [LevelDefaultVariant.NONE_RECOMMENDED_ON_TRAFFIC_SAFE]: {
    defaultLevel: null,
    recommendedLevel: ProposalLevel.TRAFFIC_SAFE,
  },
}

/** Whether the approved answer bought the action or product on a seen card. */
export enum LegalBadgeOutcome {
  ACCEPTED = "ACCEPTED",
  DECLINED = "DECLINED",
}

export const PROPOSAL_AB_TEST_VARIANTS: Record<ProposalAbTest, string[]> = {
  [ProposalAbTest.SERVICE_LEGAL_BADGE]: Object.values(LegalBadgeVariant),
  [ProposalAbTest.UPSELL_LEGAL_BADGE]: Object.values(LegalBadgeVariant),
  [ProposalAbTest.SERVICE_LEVEL_DEFAULT]: Object.values(LevelDefaultVariant),
}

export const PROPOSAL_AB_TEST_OUTCOMES: Record<ProposalAbTest, string[]> = {
  [ProposalAbTest.SERVICE_LEGAL_BADGE]: Object.values(LegalBadgeOutcome),
  [ProposalAbTest.UPSELL_LEGAL_BADGE]: Object.values(LegalBadgeOutcome),
  // The level the cyclist approved.
  [ProposalAbTest.SERVICE_LEVEL_DEFAULT]: Object.values(ProposalLevel),
}

/** What the proposal screen renders differently for the job's variants. */
export const ProposalAbTestFlagsSchema = z.object({
  isServiceLegalBadgeShown: z.boolean(),
  isUpsellLegalBadgeShown: z.boolean(),
  /**
   * The level "Välj servicenivå" starts on. Null starts on none: the
   * cyclist has to pick one before they can approve.
   */
  defaultLevel: z.nativeEnum(ProposalLevel).nullable(),
  /** The level marked "Rekommenderad", if any. */
  recommendedLevel: z.nativeEnum(ProposalLevel).nullable(),
})
export type ProposalAbTestFlags = z.infer<typeof ProposalAbTestFlagsSchema>

/** Outside the tests (the mechanic's preview), the screen is as before them. */
export const DEFAULT_PROPOSAL_AB_TEST_FLAGS: ProposalAbTestFlags = {
  isServiceLegalBadgeShown: true,
  isUpsellLegalBadgeShown: true,
  defaultLevel: ProposalLevel.TOP_CONDITION,
  recommendedLevel: null,
}

/**
 * The cyclist saw something in a test in full. `itemId` is the part for a
 * Lagkrav card in "Serviceförslag", the upsell offer for one in "Tillval".
 * The level test counts the proposal being opened, which the API records
 * itself.
 */
export const ProposalAbTestExposureSchema = z.object({
  test: z.nativeEnum(ProposalAbTest),
  itemId: z.string().uuid(),
})
export type ProposalAbTestExposure = z.infer<
  typeof ProposalAbTestExposureSchema
>

/** A job in a variant, with where to look at it. */
export interface ProposalAbTestJob {
  jobId: string
  storeName: string
  /** When the job was put in the variant. */
  assignedAt: string
  isApproved: boolean
  /** The cyclist's proposal on julo.se. */
  proposalUrl: string
  /** The job in Alfred. */
  alfredUrl: string
  /** Sentry's session replays of the cyclist's proposal. */
  sentryReplaysUrl: string
}

export interface ProposalAbTestVariantResult {
  variant: string
  label: string
  /** Jobs in this variant where the cyclist saw at least one thing in full. */
  exposedJobCount: number
  exposureCount: number
  /** Exposures on proposals not approved yet. */
  pendingCount: number
  /** Exposures per outcome, in the order of the test's `outcomes`. */
  outcomeCounts: number[]
  /** Exposed jobs whose proposal has been approved. */
  approvedJobCount: number
  /**
   * The approved service total (kr, without "Tillval") averaged over those,
   * or null before any is approved.
   */
  averageApprovedServiceTotal: number | null
  /** The exposed jobs, newest first. */
  jobs: ProposalAbTestJob[]
}

export interface ProposalAbTestResult {
  test: ProposalAbTest
  label: string
  /** What was seen in full for the test to count it. */
  exposureLabel: string
  outcomes: { outcome: string; label: string }[]
  variants: ProposalAbTestVariantResult[]
  /**
   * Chi-square test of independence between variant and outcome — for two
   * variants and two outcomes the same as a two-sided two-proportion z-test.
   * Null until at least two variants and two outcomes have something decided.
   */
  pValue: number | null
}
